import React, { useEffect, useState } from "react";
import TopNav from "@/components/TopNav";
import { Card, CardContent } from "@/components/ui/card";
import { UserCog, Users, Activity, CircleDashed } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function CoordinatorDashboard() {
    const { user } = useAuth();
    const [families, setFamilies] = useState([]);
    const [stats, setStats] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const [f, s] = await Promise.all([
                    api.get("/coordinator/families"),
                    api.get("/coordinator/overview"),
                ]);
                setFamilies(f.data);
                setStats(s.data);
            } catch { /* noop */ }
        })();
    }, []);

    return (
        <div className="min-h-screen bg-stone-50">
            <TopNav />
            <main data-testid="coordinator-dashboard" className="max-w-6xl mx-auto px-6 lg:px-8 py-10 animate-fade-in">
                <div className="mb-8">
                    <div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-600 mb-2">Coordinator</div>
                    <h1 className="font-outfit text-3xl sm:text-4xl font-semibold tracking-tight text-stone-900 flex items-center gap-3">
                        <UserCog className="w-7 h-7 text-emerald-700" />
                        {user?.full_name}
                    </h1>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                    <Stat icon={Users} label="Families" value={stats?.family_count ?? "—"} />
                    <Stat icon={CircleDashed} label="Open requests" value={stats?.open_requests ?? "—"} />
                    <Stat icon={Activity} label="In progress" value={stats?.in_progress_requests ?? "—"} />
                    <Stat icon={Activity} label="Active visits" value={stats?.active_visits ?? "—"} accent="emerald" />
                </div>

                <Card className="rounded-2xl border-stone-200">
                    <CardContent className="p-6">
                        <h3 className="font-outfit text-xl font-semibold text-stone-900 mb-4">Assigned families</h3>
                        <div className="space-y-2">
                            {families.length === 0 && (
                                <p className="text-sm text-stone-500 font-dmsans">
                                    No families assigned yet. Use the API <code className="text-xs bg-stone-100 px-1.5 py-0.5 rounded">POST /api/coordinator/assign-family</code> to assign one.
                                </p>
                            )}
                            {families.map((f) => (
                                <div key={f.id} className="p-3 border border-stone-200 rounded-xl flex items-center justify-between text-sm">
                                    <div>
                                        <div className="font-medium text-stone-900">{f.full_name}</div>
                                        <div className="text-xs text-stone-500 font-dmsans">
                                            {f.email} · Caring for {f.care_recipient_name || "—"} · {f.family_state || "—"}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            </main>
        </div>
    );
}

function Stat({ icon: Icon, label, value, accent = "stone" }) {
    const bg = accent === "emerald" ? "bg-emerald-50 text-emerald-700" : "bg-stone-100 text-stone-700";
    return (
        <Card className="rounded-2xl border-stone-200">
            <CardContent className="p-5 flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${bg}`}>
                    <Icon className="w-5 h-5" strokeWidth={1.75} />
                </div>
                <div>
                    <div className="text-xs font-bold uppercase tracking-[0.2em] text-stone-500">{label}</div>
                    <div className="font-outfit text-2xl font-semibold text-stone-900">{value}</div>
                </div>
            </CardContent>
        </Card>
    );
}
